import { Box, BoxProps, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { activateOnKeyDown, focusVisible } from '../helpers';
import { palettes, tokens } from '../theme/tokens';
import { Paragraph } from './Text';

const ContainerEnabled = css<{ checked?: boolean }>`
	color: ${tokens.text.inverse};
	cursor: pointer;

	${(props) =>
		props.checked
			? `
		background: ${tokens.text.primary};
		border-color: ${tokens.text.primary};

		&:hover,
		&[data-preview='hover'] {
			background: ${palettes.heatherGray['800']};
			border-color: ${palettes.heatherGray['800']};
		}
	`
			: `
		&:hover,
		&[data-preview='hover'] {
			border-color: ${tokens.border.focus};
		}
	`}

	${focusVisible}
`;

const ContainerDisabled = css`
	border-color: ${tokens.border.default};
	background: ${palettes.heatherGray['100']};
	color: ${tokens.text.disabled};
	cursor: not-allowed;
	pointer-events: none;
`;

const Container = styled(Box)<{ checked?: boolean; disabled?: boolean }>`
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.strong};
	border-radius: ${tokens.radius.full}px;
	padding: 0;
	box-sizing: border-box;

	${(props) => (props.disabled ? ContainerDisabled : ContainerEnabled)}
`;

const CheckedCircleIcon = styled.div`
	width: 6px;
	height: 6px;
	border-radius: 50%;
	background-color: currentColor;
`;

export function Radio(
	props: BoxProps & {
		checked?: boolean;
		disabled?: boolean;
		onChange: () => void;
		tabIndex?: number;
		'data-preview'?: string;
	},
) {
	const { checked, disabled, onChange, tabIndex, 'data-preview': dataPreview, ...rest } = props;

	const handleChange = () => {
		if (!disabled) {
			onChange();
		}
	};

	return (
		<Container
			{...rest}
			tabIndex={disabled ? -1 : (tabIndex ?? 0)}
			role="radio"
			aria-checked={!!checked}
			aria-disabled={disabled}
			checked={checked}
			disabled={disabled}
			data-preview={dataPreview}
			onClick={handleChange}
			onKeyDown={(event) => activateOnKeyDown(event, handleChange)}
		>
			{checked && <CheckedCircleIcon />}
		</Container>
	);
}

const RadioCardContainer = styled.div<{ $checked?: boolean }>`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	box-sizing: border-box;
	padding: ${tokens.gap.element}px;
	background: ${(props) => (props.$checked ? tokens.surface.primary : tokens.surface.white)};
	border: 1px solid ${(props) => (props.$checked ? tokens.border.focus : tokens.border.default)};
	border-radius: ${tokens.radius.sm}px;
	color: ${tokens.text.primary};
	cursor: pointer;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: 1;

	${Container} {
		pointer-events: none;
		flex-shrink: 0;
	}

	&:hover,
	&[data-preview='hover'] {
		background: ${tokens.surface.primary};
		border-color: ${(props) => (props.$checked ? tokens.border.focus : tokens.border.strong)};
	}

	${(props) =>
		!props.$checked &&
		`
		&:hover ${Container},
		&[data-preview='hover'] ${Container} {
			border-color: ${tokens.border.focus};
		}
	`}

	${focusVisible}
`;

function RadioCard(props: {
	checked?: boolean;
	onChange: () => void;
	title: string;
	'data-preview'?: string;
}) {
	const { checked, onChange, title, ...rest } = props;

	return (
		<RadioCardContainer
			{...rest}
			$checked={checked}
			role="radio"
			aria-checked={!!checked}
			tabIndex={0}
			onClick={onChange}
			onKeyDown={(event) => activateOnKeyDown(event, onChange)}
		>
			<Radio checked={checked} tabIndex={-1} aria-hidden onChange={() => {}} />
			{title}
		</RadioCardContainer>
	);
}

Radio.Card = RadioCard;

const OptionTitle = styled(Paragraph)`
	font-weight: ${tokens.typography.weights.medium};
	color: ${tokens.text.primary};
`;

const RadioOptionContainer = styled(Flex)`
	cursor: pointer;

	${focusVisible}
`;

function RadioOption(
	props: BoxProps & { checked?: boolean; onChange: () => void; title: string; description: string },
) {
	const { checked, onChange, title, description, ...rest } = props;

	return (
		<RadioOptionContainer
			{...rest}
			role="radio"
			aria-checked={!!checked}
			tabIndex={0}
			alignItems="center"
			py={2}
			onClick={onChange}
			onKeyDown={(event) => activateOnKeyDown(event, onChange)}
			color={undefined}
		>
			<Box mr={3} flexShrink={0} style={{ pointerEvents: 'none' }}>
				<Radio checked={checked} tabIndex={-1} aria-hidden onChange={() => {}} />
			</Box>
			<Box>
				<OptionTitle>{title}</OptionTitle>
				<Paragraph>{description}</Paragraph>
			</Box>
		</RadioOptionContainer>
	);
}

Radio.Option = RadioOption;
